import { BodyText, Tooltip } from '@postscript/components';
import styled, { css } from 'styled-components';
import { MessageItemProps } from '../types';
import { getMessageType } from '../utils/messageUtils';

export const MediaFlexWrapper = styled.div<MessageItemProps>`
  padding: ${({ incoming }) =>
    incoming
      ? '0 var(--spacing-7) 0 var(--spacing-5)'
      : '0 var(--spacing-5) 0 var(--spacing-7)'};
  max-width: 192px;
  margin: 0 0 var(--spacing-1);
  margin-left: ${({ incoming }) => (incoming ? '0' : 'auto')};
`;

export const MediaThreadPreview = styled.img<
  MessageItemProps & { hasText?: boolean }
>`
  display: block;
  border-radius: var(--border-radius-small);
  ${({ incoming, hasText }) =>
    !hasText && `border-bottom-${incoming ? 'left' : 'right'}-radius: 0;`}
  width: 100%;
  cursor: pointer;
`;

export const MessageFlexWrapper = styled.div<MessageItemProps>`
  display: flex;
  padding: ${({ incoming }) =>
    incoming
      ? '0 var(--spacing-7) 0 var(--spacing-5)'
      : '0 var(--spacing-5) 0 var(--spacing-7)'};
  justify-content: ${({ incoming }) => (incoming ? 'flex-start' : 'flex-end')};
  position: relative;

  & .message-avatar {
    margin: 0;
    position: absolute;
    bottom: 0;
    ${({ incoming }) => (incoming ? 'left: 0;' : 'right: 0;')}
  }
`;

export const MessageColumn = styled.div<MessageItemProps>`
  display: flex;
  flex-direction: column;
  width: max-content;
  max-width: 100%;
  ${({ incoming }) => (incoming ? 'margin-right: auto;' : 'margin-left: auto;')}
`;

export const StyledMessageBubble = styled.div<
  MessageItemProps & {
    $isSender?: boolean;
    $drawerActive?: boolean;
    $isHovered?: boolean;
  }
>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  border: ${({ isScheduledMessage, $isSender, $drawerActive, $isHovered }) => {
    if (isScheduledMessage) return '1px dashed var(--border-color)';
    if ($isSender) {
      if ($drawerActive || $isHovered) return '1px solid var(--blue-core)';
      return '1px solid transparent';
    }
    return 'none';
  }};
  border-radius: var(--border-radius-medium);
  border-bottom-${({ incoming }) => (incoming ? 'left' : 'right')}-radius: 0;
  padding: var(--spacing-1) var(--spacing-2);
  background: ${({ isScheduledMessage, incoming, messageType }) =>
    isScheduledMessage
      ? 'transparent'
      : `var(--color-message-bubble-${getMessageType({
          incoming,
          messageType,
        })})`};
  color: var(--color-message-text-${({ incoming, messageType }) =>
    getMessageType({ incoming, messageType })});
  word-break: break-word;
  text-align: left;

  ${({ $isSender, $drawerActive, $isHovered }) =>
    $isSender && ($drawerActive || $isHovered)
      ? 'box-shadow: 0px 0px 5px 0px var(--blue-core);'
      : 'box-shadow: none;'}

  transition: box-shadow 0.15s, border 0.15s;
`;

export const StyledBodyText = styled(BodyText)<{ truncate?: boolean }>`
  display: inline;

  &:empty {
    display: none;
  }

  ${({ truncate }) =>
    truncate &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 10;
      line-clamp: 10;
      -webkit-box-orient: vertical;
    `}

  & a {
    color: currentColor;
    text-decoration: underline;

    &:visited {
      opacity: 0.85;
    }
    &:hover {
      opacity: 0.65;
    }
    &:active {
      opacity: 0.5;
    }
    &:focus-visible {
      color: var(--purple-core);
    }
  }
`;

export const StyledAvatar = styled.div<MessageItemProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  font: var(--body-text-small);
  font-weight: var(--body-text-bold-font-weight);
  background: transparent;
  background: var(
    --color-message-bubble-${({ incoming, messageType }) => getMessageType({ incoming, messageType })}
  );
  color: var(
    --color-message-text-${({ incoming, messageType }) => getMessageType({ incoming, messageType })}
  );
  width: 24px;
  height: 24px;
  border-radius: var(--border-radius-round);
  position: absolute;
  bottom: 0;
  ${({ incoming }) => (incoming ? 'left: 0;' : 'right: 0;')}
`;

export const StyledMetadata = styled.span<MessageItemProps>`
  font: var(--body-text-normal-font-weight) 0.625rem / 0.75rem
    var(--body-text-typeface);
  color: var(
    --color-message-timestamp-${({ incoming, messageType }) => getMessageType({ incoming, messageType })}
  );
  text-align: ${({ incoming }) => (incoming ? 'left' : 'right')};
  margin-top: var(--spacing-1);
  margin-bottom: var(--spacing-2);
  padding: ${({ incoming }) =>
    incoming
      ? '0 var(--spacing-7) 0 var(--spacing-5)'
      : '0 var(--spacing-5) 0 var(--spacing-7)'};
  display: block;
`;

export const StyledAdminInfo = styled.div<MessageItemProps>`
  display: flex;
  flex-direction: row;
  text-align: ${({ incoming }) => (incoming ? 'left' : 'right')};
  gap: var(--spacing-1);
  padding: ${({ incoming }) =>
    incoming
      ? 'var(--spacing-1) var(--spacing-7) 0 0'
      : 'var(--spacing-1) var(--spacing-5) 0 var(--spacing-7)'};
  align-items: ${({ incoming }) => (incoming ? 'flex-start' : 'flex-end')};
  justify-content: ${({ incoming }) => (incoming ? 'flex-start' : 'flex-end')};
  margin: var(--spacing-1) 0 var(--spacing-4) 0;
`;

export const StyledAdminInfoItem = styled.div<MessageItemProps>`
  font: var(--body-text-normal-font-weight) 0.625rem / 0.75rem
    var(--body-text-typeface);
  color: var(
    --color-message-timestamp-${({ incoming, messageType }) => getMessageType({ incoming, messageType })}
  );
  align-self: ${({ incoming }) => (incoming ? 'flex-start' : 'flex-end')};
`;

export const StyledTooltip = styled(Tooltip)`
  max-width: 16rem;
`;
